import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Flex,
  Input,
  Text,
  Textarea,
} from '@optiaxiom/react';

import type { EvaluationSample, EvaluationSampleCreate } from '@frontend/types';

type EvaluationSampleFormProps = {
  initialValues?: EvaluationSample;
  onSave: (values: EvaluationSampleCreate) => void;
  onCancel: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSaving?: boolean;
};

export function EvaluationSampleForm({
  initialValues,
  onSave,
  onCancel,
  open,
  onOpenChange,
  isSaving = false,
}: EvaluationSampleFormProps) {
  const editing = !!initialValues;
  const [sample, setSample] = useState<EvaluationSampleCreate>(
    initialValues ?? {
      name: '',
      output: '',
      input_prompt: null,
      description: null,
    }
  );

  // Reset form state when dialog opens or initialValues change
  useEffect(() => {
    if (open) {
      setSample(
        initialValues ?? {
          name: '',
          output: '',
          input_prompt: null,
          description: null,
        }
      );
    }
  }, [open, initialValues]);

  const handleSave = () => {
    if (!sample.name.trim() || !sample.output.trim()) {
      return;
    }
    onSave(sample);
  };

  const isSaveDisabled = !sample.name.trim() || !sample.output.trim() || isSaving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>{editing ? 'Edit Example' : 'Add Example'}</DialogHeader>
        <DialogBody>
          <Flex flexDirection="column" gap="16" w="full">
            <Field label="Name" required>
              <Input
                placeholder="Enter name"
                value={sample.name}
                h="40"
                onChange={e => setSample({ ...sample, name: e.target.value })}
              />
            </Field>

            <Field label="Input Prompt" info="Optional input prompt for this evaluation sample">
              <Textarea
                placeholder="Enter the input prompt for this evaluation sample (optional)"
                value={sample.input_prompt ?? ''}
                onChange={e => setSample({ ...sample, input_prompt: e.target.value || null })}
                rows={3}
                resize="vertical"
              />
            </Field>

            <Field
              label="Output Example"
              required
              info="Provide an example output for Opal to evaluate this agent's response"
            >
              <Textarea
                placeholder="Enter expected output"
                value={sample.output}
                onChange={e => setSample({ ...sample, output: e.target.value })}
                rows={6}
                resize="vertical"
              />
            </Field>
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Flex
            flexDirection="row"
            gap="8"
            justifyContent="space-between"
            alignItems="center"
            w="full"
          >
            <Text color="fg.error" fontSize="sm">
              * Required fields
            </Text>
            <Flex flexDirection="row" gap="8">
              <Button appearance="default" onClick={onCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={isSaveDisabled}
                loading={isSaving}
              >
                {editing ? 'Update' : 'Add'}
              </Button>
            </Flex>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
